import { useState, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import apiClient from "../../services/apiClient";
import { 
  BookOpen, 
  CheckCircle,
  ArrowLeft,
  Download,
  Play,
  Pause,
  Volume2
} from "lucide-react";
import { Button } from "../ui/button";

export const ModuleDetail = () => {
  const { id } = useParams();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchModule();
    checkProgress();
    setStartTime(Date.now());
  }, [id]);

  const fetchModule = async () => {
    try {
      const response = await apiClient.get(`/user/modules/${id}`);
      setModule(response.data);
    } catch (error) {
      toast.error('Failed to fetch module');
    } finally {
      setLoading(false);
    }
  };

  const checkProgress = async () => {
    try {
      const response = await apiClient.get('/user/progress');
      const completed = response.data.some(p => p.module_id === id);
      setIsCompleted(completed);
    } catch (error) {
      console.error('Failed to check progress');
    }
  };

  const recordProgress = async () => {
    if (!startTime || isCompleted) return;

    const durationSpent = Math.floor((Date.now() - startTime) / 1000);
    
    try {
      await apiClient.post('/user/progress', {
        module_id: id,
        duration_spent: durationSpent
      });
      setIsCompleted(true);
      toast.success('Modul dokončené!');
    } catch (error) {
      console.error('Failed to record progress');
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handleSeek = (e) => {
    const newTime = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold text-charcoal-800 mb-4">Module not found</h2>
        <Link to="/portal" className="btn-primary">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Späť
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8 fade-in px-4 pb-24 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
        <Link to="/portal" className="flex items-center text-sage-600 hover:text-sage-700 transition-colors">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Späť
        </Link>
        <Button onClick={recordProgress} className="bg-sage-500 hover:bg-sage-600 text-white w-full sm:w-auto">
          <CheckCircle className="w-4 h-4 mr-2" />
          Označiť ako dokončené
        </Button>
      </div>

      {/* Module Header */}
      <div className="card p-6 sm:p-8">
        <div className="flex flex-col lg:flex-row items-start justify-between mb-6">
          <div className="flex-1 w-full">
            <div className="flex items-center space-x-4 mb-4">
              {module.image_filename ? (
                <img 
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/images/${module.image_filename}`}
                  alt={module.title}
                  className="w-16 sm:w-20 h-16 sm:h-20 object-cover rounded-lg"
                />
              ) : (
                <div className="p-2 sm:p-3 bg-sage-100 rounded-lg text-sage-600">
                  <BookOpen className="w-6 sm:w-8 h-6 sm:h-8" />
                </div>
              )}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-charcoal-800 font-display mb-4">
              {module.title}
            </h1>
            <p className="text-base sm:text-lg text-charcoal-600">
              {module.description}
            </p>
          </div>
        </div>

        {/* Audio Player */}
        {module.audio_filename && (
          <div className="bg-cream-50 rounded-xl p-4 sm:p-6 mb-6">
            {/* Hidden audio element */}
            <audio
              ref={audioRef}
              src={`${process.env.REACT_APP_BACKEND_URL}/api/audio/${module.audio_filename}`}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              preload="metadata"
            />
            
            {/* Custom Audio Player UI */}
            <div className="flex flex-col space-y-4">
              {/* Controls Row */}
              <div className="flex items-center space-x-4">
                {/* Play/Pause Button */}
                <button
                  onClick={togglePlayPause}
                  className="w-12 h-12 sm:w-14 sm:h-14 bg-sage-500 hover:bg-sage-600 rounded-full flex items-center justify-center text-white transition-colors flex-shrink-0 shadow-md"
                  data-testid="audio-play-btn"
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5 sm:w-6 sm:h-6" />
                  ) : (
                    <Play className="w-5 h-5 sm:w-6 sm:h-6 ml-1" />
                  )}
                </button>
                
                {/* Progress Section */}
                <div className="flex-1 min-w-0">
                  {/* Time Display */}
                  <div className="flex justify-between text-sm text-charcoal-600 mb-2">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                  
                  {/* Slider */}
                  <input
                    type="range"
                    min="0"
                    max={duration || 0}
                    value={currentTime}
                    onChange={handleSeek}
                    className="w-full h-3 bg-sage-200 rounded-full appearance-none cursor-pointer slider-thumb"
                    style={{
                      background: `linear-gradient(to right, #87a96b ${(currentTime / duration) * 100 || 0}%, #e1e9d9 ${(currentTime / duration) * 100 || 0}%)`
                    }}
                  />
                </div>
                
                {/* Volume Icon */}
                <div className="hidden sm:flex items-center text-sage-600">
                  <Volume2 className="w-5 h-5" />
                </div>
              </div>
            </div>
            
            {/* Audio Download Button */}
            {module.audio_downloadable && (
              <div className="mt-4 flex justify-center sm:justify-end">
                <a 
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/audio-download/${module.audio_filename}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-sage-500 hover:bg-sage-600 text-white px-5 py-2.5 rounded-lg flex items-center justify-center space-x-2 transition-colors text-sm font-medium w-full sm:w-auto"
                  data-testid="audio-download-btn"
                >
                  <Download className="w-4 h-4" />
                  <span>Audio stiahnúť</span>
                </a>
              </div>
            )}
          </div>
        )}

        {/* YouTube Video */}
        {module.youtube_url && (
          <div className="mt-6">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full rounded-lg"
                src={module.youtube_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                title="YouTube video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        )}
      </div>

      {/* Module Content */}
      <div className="card p-6 sm:p-8">
        <div className="module-content prose prose-sm sm:prose-lg max-w-none">
          {module.text_content.split('\n').map((paragraph, index) => {
            if (paragraph.trim() === '') return null;
            
            if (paragraph.trim().startsWith('**') && paragraph.trim().endsWith('**')) {
              const headingText = paragraph.trim().slice(2, -2);
              return (
                <h3 key={index} className="text-lg sm:text-xl font-semibold text-sage-800 mt-6 sm:mt-8 mb-4 font-display">
                  {headingText}
                </h3>
              );
            }
            
            return (
              <p key={index} className="mb-4 leading-relaxed text-sm sm:text-base">
                {paragraph}
              </p>
            );
          })}
        </div>
      </div>

      {/* Download Section */}
      {(module.download_filename || module.download_filename_2) && (
        <div className="card p-4 sm:p-8">
          <h2 className="text-lg sm:text-2xl font-semibold text-charcoal-800 font-display mb-4">
            Stiahnúť
          </h2>
          <div className="space-y-4">
            {module.download_filename && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-cream-50 p-4 rounded-lg gap-4">
                <div className="flex items-center space-x-3 min-w-0 flex-1">
                  <Download className="w-5 h-5 sm:w-6 sm:h-6 text-sage-600 flex-shrink-0" />
                  <span className="text-charcoal-700 text-sm sm:text-base truncate">{module.download_filename}</span>
                </div>
                <a 
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/download/${module.download_filename}`}
                  download
                  className="bg-sage-500 hover:bg-sage-600 text-white px-5 py-2.5 rounded-lg flex items-center justify-center space-x-2 transition-colors w-full sm:w-auto flex-shrink-0"
                >
                  <Download className="w-4 h-4" />
                  <span>Stiahnúť</span>
                </a>
              </div>
            )}
            {module.download_filename_2 && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-cream-50 p-4 rounded-lg gap-4">
                <div className="flex items-center space-x-3 min-w-0 flex-1">
                  <Download className="w-5 h-5 sm:w-6 sm:h-6 text-sage-600 flex-shrink-0" />
                  <span className="text-charcoal-700 text-sm sm:text-base truncate">{module.download_filename_2}</span>
                </div>
                <a 
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/download/${module.download_filename_2}`}
                  download
                  className="bg-sage-500 hover:bg-sage-600 text-white px-5 py-2.5 rounded-lg flex items-center justify-center space-x-2 transition-colors w-full sm:w-auto flex-shrink-0"
                >
                  <Download className="w-4 h-4" />
                  <span>Stiahnúť</span>
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Section */}
      <div className="card p-6 sm:p-8 text-center">
        <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
          {isCompleted ? (
            <Button disabled className="bg-green-500 text-white w-full sm:w-auto cursor-default">
              <CheckCircle className="w-4 h-4 mr-2" />
              Už dokončené
            </Button>
          ) : (
            <Button onClick={recordProgress} className="bg-sage-500 hover:bg-sage-600 text-white w-full sm:w-auto">
              <CheckCircle className="w-4 h-4 mr-2" />
              Označiť ako dokončené
            </Button>
          )}
          <Link to="/portal" className="w-full sm:w-auto">
            <Button variant="outline" className="text-sage-600 border-sage-200 hover:bg-sage-50 w-full">
              Ďalšie moduly
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};
